import * as XLSX from 'xlsx';
import { Box, Button, Card, CircularProgress, Container, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import React, { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { ScheduledReport } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import SimpleDialog from "src/components/simple-dialog";
import DisplayErrors from "src/components/display-errors";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxTextField from "src/components/inputs/trux-text-field";
import { addMonthsToDate, formatDateForPreview, subtractDates, today } from "src/utils/date-utils";
import TruxNumberField from "src/components/inputs/trux-number-field";
import TruxDecimalTwoField from "src/components/inputs/trux-decimal-two-field";
import TruxDateField from "src/components/inputs/trux-date-field";
import { formatDecimalTwoAsString } from "src/utils/decimal-two-utils";
import TruxSimpleTextField from 'src/components/inputs/trux-simple-text-field';
import { TruxDeleteTableCell } from 'src/components/trux-delete-table-cell';
import TruxSelectField from 'src/components/inputs/trux-select-field';
import { ReportRunnerConfigurator, useDaysDormantFilter, useLandmarkFilter, useReportRunnerConfigurator } from '../reports/runner/report-runner';

type ScheduledReportsProps = {
  scheduledReports?: ScheduledReport[],
  onScheduledReportSelected: (a: ScheduledReport) => void,
  onScheduledReportDeleteSelected: (a: ScheduledReport) => void,
}

type ScheduledReportType = {
  scheduledReportType: number,
  scheduledReportName: string,
}

type ScheduledReportInterval = {
  scheduledReportInterval: number,
  intervalName: string,
}

const scheduledReportTypes: ScheduledReportType[] = [
  {
    scheduledReportType: 0,
    scheduledReportName: "Trailers per Landmark"
  },
  {
    scheduledReportType: 101,
    scheduledReportName: "Dormant Assets"
  }
];

const scheduledReportIntervals: ScheduledReportInterval[] = [
  {
    scheduledReportInterval: 0,
    intervalName: "Daily"
  }
];


function getReportName(reportType: number) {
  return scheduledReportTypes.find(s => s.scheduledReportType === reportType)?.scheduledReportName || "";
}
function getReportInterval(interval: number) {
  return scheduledReportIntervals.find(s => s.scheduledReportInterval === interval)?.intervalName || "";
}

const ScheduledReports = memo(({ scheduledReports, onScheduledReportSelected, onScheduledReportDeleteSelected }: ScheduledReportsProps) => {
  const { sortedList, sortData } = useSort(scheduledReports);

  if (!sortedList) {
    return null;
  }

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table size="small">
          <TableHead>
            <TableRow>

              <TableCell />
              <SortableHeader sortKey="scheduledReportType" sortData={sortData} >
                Report
              </SortableHeader>

              <SortableHeader sortKey="emails" sortData={sortData} >
                Emails
              </SortableHeader>

              <SortableHeader sortKey="sendingInterval" align="center" sortData={sortData} >
                Sending Interval
              </SortableHeader>

              <SortableHeader sortKey="lastSent" align="center" sortData={sortData} >
                Last Sent
              </SortableHeader>

              <SortableHeader sortKey="nextSend" align="center" sortData={sortData} >
                Next Send
              </SortableHeader>

              <SortableHeader sortKey="sendAfter" align="right" sortData={sortData} >
                Send After
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((scheduledReport, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onScheduledReportSelected(scheduledReport)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell align="left">
                <IconButton color="error" onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onScheduledReportDeleteSelected(scheduledReport)
                }}>
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </TruxTableCell>

              <TruxTableCell>{getReportName(scheduledReport.scheduledReportType)}</TruxTableCell>
              <TruxTableCell>{scheduledReport.emails.join(", ")}</TruxTableCell>
              <TruxTableCell align="center">{getReportInterval(scheduledReport.sendingInterval)}</TruxTableCell>
              <TruxTableCell align="center">{formatDateForPreview(scheduledReport.lastSent)}</TruxTableCell>
              <TruxTableCell align="center">{formatDateForPreview(scheduledReport.nextSend)}</TruxTableCell>
              <TruxTableCell align="right">{scheduledReport.sendAfter}</TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  scheduledReports?: ScheduledReport[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    scheduledReports: axios.get(endpoints.scheduledReports.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);

  const selectedScheduledReportModel = useModelSavingData<ScheduledReport>(
    endpoints.scheduledReports.update,
    endpoints.scheduledReports.create,
    endpoints.scheduledReports.delete,
  );

  const updateScheduledReport = () => {
    selectedScheduledReportModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the Scheduled Report");
        const scheduledReport = response.data as ScheduledReport;
        updateData("scheduledReports")(replaceById(data!.scheduledReports!, scheduledReport));

        setIsOpen(false);
      }
    })
  }

  const [isOpenDeleteScheduledReport, setIsOpenDeleteScheduledReport] = useState(false);

  const deleteScheduledReport = useCallback(() => {
    selectedScheduledReportModel.destroy().then((response) => {
      if (response) {
        enqueueSnackbar("Successfully deleted the Scheduled Report", { variant: "success" })
        setIsOpenDeleteScheduledReport(false);
        updateData("scheduledReports")(deleteById(data!.scheduledReports!, selectedScheduledReportModel.model!));
      }
    })
  }, [data, selectedScheduledReportModel, updateData]);

  const onScheduledReportSelected = useCallback((a: ScheduledReport) => {
    selectedScheduledReportModel.set(a);
    setIsOpen(true);
  }, [selectedScheduledReportModel, setIsOpen]);

  const onScheduledReportDeleteSelected = useCallback((a: ScheduledReport) => {
    selectedScheduledReportModel.set(a);
    setIsOpenDeleteScheduledReport(true);
  }, [selectedScheduledReportModel, setIsOpenDeleteScheduledReport]);


  const onNewScheduledReportSelected = useCallback(() => {
    selectedScheduledReportModel.set({
      id: 0,
      createdAt: undefined,
      organizationId: 0,
      scheduledReportType: 101,
      emails: [],
      request: "{}",
      sendingInterval: 0,
      lastSent: undefined,
      nextSend: undefined,
      sendAfter: "10:00",
    });
    setIsOpen(true);
  }, [selectedScheduledReportModel]);

  const newScheduledReportButton = <Button
    onClick={onNewScheduledReportSelected}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    New Scheduled Report
  </Button>;

  const daysDormantFilter = useDaysDormantFilter();
  const landmarkFilter = useLandmarkFilter();

  const filters = [
    // dateRangeFilter,
    // organizationFilter,
    daysDormantFilter,
    landmarkFilter
  ];

  const {
    run,
    reinit,
    render,
  } = useReportRunnerConfigurator(filters);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.scheduledReports?.length !== 0 &&
          <PageHeader title="Scheduled Reports">
            {newScheduledReportButton}
          </PageHeader>
        }

        {data && data?.scheduledReports?.length === 0 &&
          <NoneFound title="No ScheduledReports Found" message="To add a Scheduled Report click the 'New Scheduled Report' button below.">
            {newScheduledReportButton}
          </NoneFound>
        }

        {data && data?.scheduledReports?.length !== 0 && <ScheduledReports scheduledReports={data.scheduledReports} onScheduledReportSelected={onScheduledReportSelected} onScheduledReportDeleteSelected={onScheduledReportDeleteSelected} />}

        {selectedScheduledReportModel.model &&
          <SimpleDialog
            title="Delete Scheduled Report"
            open={isOpenDeleteScheduledReport}
            onCancel={() => setIsOpenDeleteScheduledReport(false)}
            onSubmit={deleteScheduledReport}
            submitDisabled={selectedScheduledReportModel.loading}
            submitButton="Delete Scheduled Report"
            submitButtonIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
            submitButtonColor="error">

            <DisplayErrors savingData={selectedScheduledReportModel} />

            Are you sure you would like to delete this Scheduled Report?
          </SimpleDialog>
        }

        {selectedScheduledReportModel.model && <SimpleDialog
          title={selectedScheduledReportModel.isNew ? "New Scheduled Report" : "Update Scheduled Report"}
          open={isOpen}
          onCancel={() => setIsOpen(false)}
          onSubmit={() => {
            const [request, err] = run();

            if (err) {
              enqueueSnackbar(err, {variant: "error"});
              return;
            }

            if (selectedScheduledReportModel.model?.emails.length === 0) {
              enqueueSnackbar("Please enter at least one Email", {variant: "error"});
              return;
            }

            selectedScheduledReportModel.update(model => {
              model.request = JSON.stringify(request);

              updateScheduledReport();
            });
          }}
          submitDisabled={selectedScheduledReportModel.loading}
          submitButton={selectedScheduledReportModel.isNew ? "New Scheduled Report" : "Update Scheduled Report"}
          submitButtonIcon={selectedScheduledReportModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
          submitButtonColor={selectedScheduledReportModel.isNew ? "success" : "info"}>

          <DisplayErrors savingData={selectedScheduledReportModel} />

          <Stack direction="column" gap={1}>
            <TruxFormField label="Report">
              <TruxSelectField
                modelSavingData={selectedScheduledReportModel}
                field="scheduledReportType"
                onChange={() => {
                  reinit();
                }}
                disabled={!selectedScheduledReportModel.isNew}
              >
                <MenuItem value={0}>{getReportName(0)}</MenuItem>
                <MenuItem value={101}>{getReportName(101)}</MenuItem>
              </TruxSelectField>
            </TruxFormField>

            <TruxFormField label="Interval">
              <TruxSimpleTextField value={getReportInterval(selectedScheduledReportModel.model.sendingInterval)} onChange={() => {}} disabled />
            </TruxFormField>

            {selectedScheduledReportModel.model.scheduledReportType === 101 && render(!selectedScheduledReportModel.isNew)}


            <Box sx={{ marginLeft: -3, marginRight: -3 }}>
              <Box>
                <TableContainer sx={{ maxHeight: 440 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {selectedScheduledReportModel.model.emails.length > 1 && <TruxTableCell sx={{ maxWidth: "30px" }} />}
                        <TruxTableCell>
                          Email
                        </TruxTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedScheduledReportModel.model.emails.map((email, index) => <TableRow key={index}>
                        {(selectedScheduledReportModel?.model?.emails.length || 0) > 1 &&
                          <TruxDeleteTableCell>
                            <IconButton color="error" onClick={() => {
                              selectedScheduledReportModel.update((model) => {
                                model.emails.splice(index, 1);
                              })
                            }}>
                              <Iconify icon="solar:trash-bin-trash-bold" />
                            </IconButton>
                          </TruxDeleteTableCell>
                        }
                        <TruxTableCell>
                          <TruxSimpleTextField value={email} onChange={(val) => {
                            selectedScheduledReportModel.update((model) => {
                              model.emails[index] = val;
                            })
                          }} />
                        </TruxTableCell>
                      </TableRow>)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box sx={{marginLeft: 1.5}}>
                <Button size="small" variant="soft" onClick={() => {
                  selectedScheduledReportModel.update((model) => {
                    model.emails.push("");
                  })
                }} startIcon={<Iconify icon="mingcute:add-line" />}>
                  Add Email
                </Button>
              </Box>
            </Box>
          </Stack>
        </SimpleDialog>}
      </DisplayPageData>
    </Container >
  );
}
